/**
 * The dealer workspace's CRM slice of the data provider (DataProvider.crm): the customer record, its timeline, tasks,
 * appointments, duplicates, the team and the dealer group, over the CRM core routes (switch dealer_crm on the store;
 * the API answers 404 feature_disabled while crm_core is off). Live only: the fixture tenant is a workshop, so its
 * slice answers "not switched on" the same way.
 *   /v1/app/customers*  /v1/app/crm/tasks*  /v1/app/crm/appointments*  /v1/app/users  /v1/app/roles  /v1/app/group/*  /v1/app/session/switch
 */
import { APP, ApiError, type Http } from '@app/core/http';

export type UUID = string;
export type ISO = string;

export interface CrmCustomer {
  id: UUID;
  display_name: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  phone_e164: string | null;
  alt_phone_e164: string | null;
  email: string | null;
  tags: string[];
  source: string | null;
  preferred_language: 'en' | 'es' | null;
  assigned_user_id: UUID | null;
  last_activity_at: ISO | null;
  first_seen_source: string | null;
  erased_at: ISO | null;
  archived_at: ISO | null;
  created_at: ISO;
}
export interface CrmVehicle {
  id: UUID;
  vin: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  odometer_mi: number | null;
  owner_since?: ISO | null;
}
export interface ContactRow {
  channel: 'sms' | 'voice' | 'email';
  purpose: 'informational' | 'marketing';
  allowed: boolean;
  reason: string;
  since?: ISO | null;
  source?: string | null;
}
export interface CrmProfile extends CrmCustomer {
  vehicles: CrmVehicle[];
  contactability?: ContactRow[];
  assigned_user?: { id: UUID; first_name: string | null; last_name: string | null; role: string } | null;
  redirect_to?: UUID;
}
export interface CustomerQuery {
  q?: string;
  assigned?: 'me';
  lapsed_days?: number;
  has_upcoming?: boolean;
  duplicates?: boolean;
  after?: UUID;
  limit?: number;
}
export interface Activity {
  id: UUID;
  kind: string;
  source_module: string;
  source_id: string;
  vehicle_id: UUID | null;
  occurred_at: ISO;
  actor: string;
  actor_name: string | null;
  facts: Record<string, unknown>;
  body: string | null;
}
export interface Task {
  id: UUID;
  version: number;
  kind: string;
  title: string;
  customer_id: UUID | null;
  vehicle_id: UUID | null;
  subject_type: string | null;
  subject_id: string | null;
  department: 'service' | 'sales' | 'parts' | null;
  assigned_user_id: UUID | null;
  claimed_at: ISO | null;
  claim_expires_at: ISO | null;
  due_at: ISO | null;
  status: 'open' | 'done' | 'cancelled';
  priority: 'normal' | 'high';
  completed_by: string | null;
  completed_at: ISO | null;
  outcome_note: string | null;
  closed_reason: string | null;
  customer_name?: string | null;
  assigned_name?: string | null;
  claimed: boolean;
  overdue: boolean;
}
export interface TaskQuery {
  view?: 'mine' | 'department' | 'all';
  status?: 'open' | 'done' | 'cancelled' | 'all';
  customer_id?: UUID;
  limit?: number;
}
export interface Appointment {
  id: UUID;
  version: number;
  customer_id: UUID;
  vehicle_id: UUID | null;
  department: 'service' | 'sales';
  kind: string;
  status: string;
  requested_time_text: string | null;
  starts_at: ISO | null;
  ends_at: ISO | null;
  transport: 'wait' | 'drop_off' | null;
  concern: string | null;
  source: string;
  desk_log_entry_id: UUID | null;
  external_system: string | null;
  external_ref: string | null;
  confirmed_at: ISO | null;
  confirmed_source: 'staff' | 'scheduler' | null;
  confirmed_by_name: string | null;
  cancelled_reason: string | null;
  created_at: ISO;
  customer_name?: string;
  customer_phone?: string | null;
  vehicle_label?: string | null;
}
export interface AppointmentQuery {
  from?: ISO;
  to?: ISO;
  department?: 'service' | 'sales';
  status?: string;
  customer_id?: UUID;
}
export interface DuplicatePerson {
  id: UUID;
  display_name: string;
  phone_e164: string | null;
  alt_phone_e164: string | null;
  email: string | null;
  tags: string[];
  created_at: ISO;
  last_activity_at: ISO | null;
  vehicles: number;
  calls: number;
  appointments: number;
}
export interface DuplicatePair {
  a: DuplicatePerson;
  b: DuplicatePerson;
  reasons: ('phone' | 'email' | 'flagged')[];
}
export interface MergeResult {
  survivor_id: UUID;
  merged_id: UUID;
  replayed: boolean;
  moved: Record<string, number>;
  filled: string[];
}
export interface TeamUser {
  id: UUID;
  email: string | null;
  phone_e164: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
  status: 'invited' | 'active' | 'disabled';
  departments: string[];
  last_login_at: ISO | null;
}
export interface Role {
  key: string;
  name: string;
  system: boolean;
}
export interface GroupStore {
  id: UUID;
  name: string;
  role: string;
  current: boolean;
}
export interface GroupSummary {
  group: { id: UUID; name: string } | null;
  since: ISO;
  stores: {
    id: UUID;
    name: string;
    current: boolean;
    counts: Record<'calls' | 'requests_waiting' | 'confirmed' | 'arrived' | 'tasks_overdue', number> | null;
  }[];
}

export interface Api {
  customers(q: CustomerQuery): Promise<{ items: CrmCustomer[]; next_after: UUID | null }>;
  customer(id: UUID): Promise<CrmProfile>;
  patchCustomer(id: UUID, body: Partial<Pick<CrmCustomer, 'assigned_user_id' | 'preferred_language'>>): Promise<CrmCustomer>;
  resolve(body: {
    phone?: string;
    email?: string;
    name?: string;
    dry_run?: boolean;
  }): Promise<{ customer_id: UUID | null; created: boolean; match: string; possible_duplicate: boolean }>;
  duplicates(): Promise<{ items: DuplicatePair[] }>;
  merge(body: { survivor_id: UUID; merged_id: UUID }, key: string): Promise<MergeResult>;
  timeline(
    id: UUID,
    q: { kinds?: readonly string[] | null; before?: string | null; limit?: number },
  ): Promise<{ items: Activity[]; next: string | null; redirect_to?: UUID }>;
  addNote(id: UUID, body: string): Promise<{ activity_id: UUID }>;
  tasks(q: TaskQuery): Promise<{ items: Task[]; departments: string[] }>;
  createTask(body: {
    title: string;
    customer_id?: UUID | null;
    due_at?: ISO | null;
    department?: string | null;
    assigned_user_id?: UUID | null;
    priority?: 'normal' | 'high';
  }): Promise<Task>;
  patchTask(
    id: UUID,
    body: { version: number } & Partial<Pick<Task, 'title' | 'due_at' | 'priority' | 'department' | 'assigned_user_id'>>,
  ): Promise<Task>;
  taskAction(id: UUID, action: 'claim' | 'complete' | 'reopen' | 'cancel', body?: { outcome_note?: string }): Promise<Task>;
  appointments(q: AppointmentQuery): Promise<{ items: Appointment[] }>;
  appointment(id: UUID): Promise<Appointment>;
  createAppointment(body: {
    customer_id: UUID;
    vehicle_id?: UUID | null;
    kind: string;
    requested_time_text?: string | null;
    concern?: string | null;
  }): Promise<Appointment>;
  confirmAppointment(id: UUID, body: { starts_at: ISO; external_ref?: string | null; version?: number }): Promise<Appointment>;
  rescheduleAppointment(id: UUID, body: { starts_at: ISO; version?: number }): Promise<Appointment>;
  cancelAppointment(id: UUID, body: { reason?: string | null; version?: number }): Promise<Appointment>;
  arrival(id: UUID, arrival: 'arrived' | 'no_show' | 'pending'): Promise<Appointment>;
  users(): Promise<TeamUser[]>;
  roles(): Promise<Role[]>;
  patchUser(id: UUID, body: { role?: string; departments?: string[] }): Promise<TeamUser>;
  invite(body: {
    email: string;
    first_name?: string;
    last_name?: string;
    role: string;
    departments?: string[];
  }): Promise<{ user: TeamUser }>;
  groupStores(): Promise<{ group: { id: UUID; name: string } | null; stores: GroupStore[] }>;
  groupSummary(): Promise<GroupSummary>;
  switchStore(tenantId: UUID): Promise<{ tenant: { id: UUID; name: string }; mfa_required?: boolean }>;
}

const C = `${APP}/customers`;
const T = `${APP}/crm/tasks`;
const A = `${APP}/crm/appointments`;
const e = encodeURIComponent;
const bool = (b: boolean | undefined) => (b ? 'true' : undefined);

export const live = (http: Http): Api => ({
  customers: (q) =>
    http.get(C, {
      query: {
        q: q.q,
        assigned: q.assigned,
        lapsed_days: q.lapsed_days,
        has_upcoming: bool(q.has_upcoming),
        duplicates: bool(q.duplicates),
        after: q.after,
        limit: q.limit,
      },
    }),
  customer: (id) => http.get<{ customer: CrmProfile }>(`${C}/${e(id)}`).then((r) => r.customer),
  patchCustomer: (id, body) => http.patch<{ customer: CrmCustomer }>(`${C}/${e(id)}`, body).then((r) => r.customer),
  resolve: (body) => http.post(`${C}/resolve`, body),
  duplicates: () => http.get(`${C}/duplicates`),
  merge: (body, key) => http.post(`${C}/merge`, body, { idempotencyKey: key }),
  timeline: (id, q) =>
    http.get(`${C}/${e(id)}/timeline`, {
      query: { kinds: q.kinds?.length ? q.kinds.join(',') : undefined, before: q.before ?? undefined, limit: q.limit },
    }),
  addNote: (id, body) => http.post(`${C}/${e(id)}/notes`, { body }),
  tasks: (q) => http.get(T, { query: { ...q } }),
  createTask: (body) => http.post<{ task: Task }>(T, body).then((r) => r.task),
  patchTask: (id, body) => http.patch<{ task: Task }>(`${T}/${e(id)}`, body).then((r) => r.task),
  taskAction: (id, action, body) => http.post<{ task: Task }>(`${T}/${e(id)}/${action}`, body ?? {}).then((r) => r.task),
  appointments: (q) => http.get(A, { query: { ...q } }),
  appointment: (id) => http.get<{ appointment: Appointment }>(`${A}/${e(id)}`).then((r) => r.appointment),
  createAppointment: (body) => http.post<{ appointment: Appointment }>(A, body).then((r) => r.appointment),
  confirmAppointment: (id, body) => http.post<{ appointment: Appointment }>(`${A}/${e(id)}/confirm`, body).then((r) => r.appointment),
  rescheduleAppointment: (id, body) => http.post<{ appointment: Appointment }>(`${A}/${e(id)}/reschedule`, body).then((r) => r.appointment),
  cancelAppointment: (id, body) => http.post<{ appointment: Appointment }>(`${A}/${e(id)}/cancel`, body).then((r) => r.appointment),
  arrival: (id, arrival) => http.post<{ appointment: Appointment }>(`${A}/${e(id)}/arrival`, { arrival }).then((r) => r.appointment),
  users: () => http.get<{ users: TeamUser[] }>(`${APP}/users`).then((r) => r.users),
  roles: () => http.get<{ roles: Role[] }>(`${APP}/roles`).then((r) => r.roles),
  patchUser: (id, body) => http.patch<{ user: TeamUser }>(`${APP}/users/${e(id)}`, body).then((r) => r.user),
  invite: (body) => http.post(`${APP}/users/invite`, body),
  groupStores: () => http.get(`${APP}/group/stores`),
  groupSummary: () => http.get(`${APP}/group/summary`),
  switchStore: (tenantId) => http.post(`${APP}/session/switch`, { tenant_id: tenantId }),
});

/** The fixture tenant is a workshop: every CRM call answers as the API does while the switch is off. */
export const fixture = (): Api => {
  const off = () => Promise.reject(new ApiError(404, 'feature_disabled', null, { ok: false, error: 'feature_disabled' }));
  return new Proxy({} as Api, { get: () => off });
};
